// The opening-hours READ model for a roomy surface (2026-10-23, owner: "opening
// hours has earned a better compressed layout"): the week Monday-first, days with
// the same hours merged ("Mon–Fri 09–20"), and whether the place is open now.
// Pure, no React; the week comes from openingHoursWeek (the one parser).
import { ISO_WEEK } from '../display/projections'
import { dayName, type DayHours, type Week } from './openingHoursWeek'

export interface WeekRow {
  /** "Mon–Fri", "Sat" */
  days: string
  /** stored day numbers in the row (0 = Sunday) */
  members: number[]
  /** "09–20", "09–12, 13–17", "Open 24 hours", null = closed */
  hours: string | null
}

const short = (t: string) => (t.endsWith(':00') ? t.slice(0, 2) : t)

export function hoursText(d: DayHours): string | null {
  if (d.mode === 'closed') return null
  if (d.mode === '24h') return 'Open 24 hours'
  const parts = d.ranges.filter((r) => r.open && r.close).map((r) => `${short(r.open)}–${short(r.close)}`)
  return parts.length ? parts.join(', ') : null
}

/** Monday-first rows, consecutive days with identical hours merged. */
export function weekRows(week: Week): WeekRow[] {
  const rows: WeekRow[] = []
  for (const day of ISO_WEEK) {
    const hours = hoursText(week[day])
    const last = rows[rows.length - 1]
    if (last && last.hours === hours) last.members.push(day)
    else rows.push({ days: '', members: [day], hours })
  }
  for (const r of rows) {
    const first = dayName(r.members[0]), lastDay = dayName(r.members[r.members.length - 1])
    r.days = r.members.length === 1 ? first : `${first}–${lastDay}`
  }
  return rows
}

const minutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3))

/** The stored weekday (0 = Sunday) and minute of day of `now` in `zone`. */
export function localDayMinute(now: Date, zone: string): { day: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'))
  return { day, minute: Number(get('hour')) * 60 + Number(get('minute')) }
}

/** Open at `now` in `zone`: today's spans, or yesterday's overnight spill. */
export function isOpenNow(week: Week, now: Date, zone: string): boolean {
  const { day, minute } = localDayMinute(now, zone)
  const today = week[day]
  if (today?.mode === '24h') return true
  if (today?.mode === 'hours') {
    for (const r of today.ranges) {
      const o = minutes(r.open), c = minutes(r.close)
      if (c > o ? minute >= o && minute < c : minute >= o) return true
    }
  }
  const prev = week[(day + 6) % 7]
  if (prev?.mode === 'hours') {
    for (const r of prev.ranges) {
      const o = minutes(r.open), c = minutes(r.close)
      if (c <= o && minute < c) return true
    }
  }
  return false
}
